import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import {
  Subject,
  Observable,
  tap,
  catchError,
  throwError,
  switchMap,
  from,
  map,
  of,
} from 'rxjs'
import { sessionLSKey } from '../constants/auth'
import { AuthService } from '../services/auth.service'
import { ErrorService } from '../services/error.service'
import { ApiError } from '../types/apiError'
import { Session, Tokens } from '../types/auth'
import { INVALID_ACCESS_TOKEN, SESSION_EXPIRED_ERR } from '../constants/errors'

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  authService = inject(AuthService)
  errorService = inject(ErrorService)
  router = inject(Router)

  isRefreshing = false
  tokenRefreshedSource = new Subject<void>()

  /**
   * Adds authentication headers and handles token refreshes and expired sessions.
   *
   * @param {HttpRequest<unknown>} req The outgoing HTTP request.
   * @param {HttpHandler} next The next handler in the interceptor chain.
   * @returns {Observable<HttpEvent<unknown>>} The request events, including any retry after refreshing the access token.
   */
  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // set Authorization header
    req = this.setAuthHeader(req)

    // handle response
    return next.handle(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          // refresh access token when close to expiry. We know it will be close to expiry when the Response Header 'Token-Expiry' is set from the api
          const tokenExpiring = event.headers.has('token-expiring')
          if (tokenExpiring) {
            this.refreshToken(req, next).subscribe() // Trigger refresh in the background
          }
        }
      }),
      catchError((httpErr: HttpErrorResponse) =>
        this.normalizeError(httpErr).pipe(
          switchMap((normalizedErr) => {
            const apiErr = normalizedErr.error?.error as ApiError

            if (apiErr) {
              if (apiErr.name === INVALID_ACCESS_TOKEN) {
                return this.refreshToken(req, next)
              }
              if (apiErr.name === SESSION_EXPIRED_ERR) {
                this.handleInvalidSession()
              }
            }
            return throwError(() => normalizedErr)
          }),
        ),
      ),
    )
  }

  /**
   * Decodes JSON error bodies returned as Blobs for file download requests.
   * Preserves the response metadata and leaves non-JSON errors unchanged.
   *
   * @param {HttpErrorResponse} httpErr The failed response, which may contain a Blob error body.
   * @returns {Observable<HttpErrorResponse>} The error with its JSON body decoded, or the original error if decoding is unnecessary or fails.
   */
  private normalizeError(
    httpErr: HttpErrorResponse,
  ): Observable<HttpErrorResponse> {
    if (!(httpErr?.error instanceof Blob)) return of(httpErr)

    return from(httpErr.error.text()).pipe(
      map(
        (body) =>
          new HttpErrorResponse({
            error: JSON.parse(body),
            headers: httpErr.headers,
            status: httpErr.status,
            statusText: httpErr.statusText,
            url: httpErr.url ?? undefined,
          }),
      ),
      // Non-JSON proxy/network responses keep their original failure details.
      catchError(() => of(httpErr)),
    )
  }

  /**
   * Sets Authorization header with bearer token for all requests (when a user is logged in)
   * @param {HttpRequest<unknown>} request The HTTP request
   * @returns {HttpRequest<unknown>} The request with the Authorization header and bearer token attached
   */
  private setAuthHeader(request: HttpRequest<unknown>): HttpRequest<unknown> {
    if (this.authService.isLoggedIn()) {
      const accessToken = this.authService.accessToken()
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
    }
    return request
  }

  /**
   * Refreshes the access token before retrying this request and any queued requests.
   * Logs the user out if the refresh token has expired.
   *
   * @param {HttpRequest<unknown>} request The request to retry with the refreshed token.
   * @param {HttpHandler} next The next handler in the interceptor chain.
   * @returns {Observable<HttpEvent<unknown>>} The retried request events, or an error if the refresh or retry fails.
   */
  private refreshToken(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true
      const refreshWaiters = this.tokenRefreshedSource
      return this.authService.refreshAccessToken().pipe(
        switchMap((res) => {
          const sessionLS = localStorage.getItem(sessionLSKey)
          if (sessionLS) {
            const session = JSON.parse(sessionLS) as Session
            session.accessToken = res.accessToken
            localStorage.setItem(sessionLSKey, JSON.stringify(session))

            const tokens: Tokens = {
              refreshToken: session.refreshToken,
              accessToken: session.accessToken,
            }
            this.authService.setTokens(tokens)
          }

          // Queued downloads must see the new token before they retry.
          this.isRefreshing = false
          this.tokenRefreshedSource = new Subject<void>()
          refreshWaiters.next()
          refreshWaiters.complete()

          request = this.setAuthHeader(request)
          return next.handle(request)
        }),
        catchError((err) => {
          const apiErr = (err.error?.error as ApiError) || (err as ApiError)

          if (apiErr) {
            // if there's an error in the refresh token process, just log out the user and clear the cache
            if (apiErr.name === SESSION_EXPIRED_ERR) {
              this.handleInvalidSession()
            }
          }

          // returning this in the form of an HttpError
          const httpErr = {
            error: { error: apiErr },
          }
          if (this.tokenRefreshedSource === refreshWaiters) {
            this.isRefreshing = false
            this.tokenRefreshedSource = new Subject<void>()
            refreshWaiters.error(httpErr)
          }
          return throwError(() => httpErr)
        }),
      )
    } else {
      // below handles queuing requests until the access token is refreshed
      return this.tokenRefreshedSource.pipe(
        switchMap(() => next.handle(this.setAuthHeader(request))),
      )
    }
  }

  /**
   * Removes session data from local storage and reroutes user to login screen
   */
  private handleInvalidSession() {
    this.authService.removeSessionFromLS()
    const returnUrl = this.router.url
    this.router.navigate(['/auth/login'], {
      queryParams:
        /^\/(?:requisitions|purchase-orders)(?:\/[a-f0-9-]{36})?$/.test(
          returnUrl,
        )
          ? { returnUrl }
          : undefined,
    })
  }
}
